import React, { useState, useEffect } from 'react';
import { DocumentUpload } from '../documents/DocumentUpload';
import { MultipleDocumentUpload } from '../documents/MultipleDocumentUpload';
import { CheckCircleIcon, ExclamationTriangleIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { Document } from './RegistrationWizard';

interface DocumentType {
    type: string;
    description: string;
    required: boolean;
}

interface DocumentsStepProps {
    documents: Document[];
    onDocumentsUpdate: (documents: Document[]) => void;
    isReadOnly?: boolean;
}

export const DocumentsStep: React.FC<DocumentsStepProps> = ({
    documents,
    onDocumentsUpdate,
    isReadOnly = false
}) => {
    const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasLoadedDocuments, setHasLoadedDocuments] = useState(false);

    const defaultDocumentTypes: DocumentType[] = React.useMemo(() => [
        { type: 'DIRECTOR_APPOINTMENT_ORDER', description: 'Приказ о назначении директора', required: true },
        { type: 'COMPANY_CHARTER', description: 'Устав', required: true },
        { type: 'POWER_OF_ATTORNEY', description: 'Доверенность', required: false },
        { type: 'ADDITIONAL_DOCUMENT', description: 'Дополнительный документ', required: false }
    ], []);

    const loadDocumentTypes = React.useCallback(async () => {
        try {
            const response = await apiService.getDocumentTypes();
            if (response.data.success) {
                const types = Object.entries((response.data as any).types).map(([type, description]) => ({
                    type,
                    description: description as string,
                    required: defaultDocumentTypes.find(dt => dt.type === type)?.required || false
                }));
                setDocumentTypes(types);
            } else {
                setDocumentTypes(defaultDocumentTypes);
            }
        } catch (error) {
            console.error('Ошибка загрузки типов документов:', error);
            setDocumentTypes(defaultDocumentTypes);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const loadExistingDocuments = React.useCallback(async () => {
        if (hasLoadedDocuments) return;

        try {
            const response = await apiService.getMyDocuments();
            console.log('API Response for documents:', response);
            if (response.data.success) {
                const documents = (response.data as any).documents || [];
                console.log('Loaded documents:', documents);
                onDocumentsUpdate(documents);
                setHasLoadedDocuments(true);
            }
        } catch (error) {
            console.error('Ошибка загрузки документов:', error);
        }
    }, [onDocumentsUpdate, hasLoadedDocuments]);

    useEffect(() => {
        loadDocumentTypes();
    }, []);

    useEffect(() => {
        if (documents.length === 0 && !hasLoadedDocuments) {
            loadExistingDocuments();
        }
    }, [documents.length, loadExistingDocuments, hasLoadedDocuments]);

    const handleUploadSuccess = (document: Document) => {
        const updatedDocuments = documents.filter(d => d.type !== document.type);
        updatedDocuments.push(document);
        onDocumentsUpdate(updatedDocuments);
    };

    const handleDeleteSuccess = (documentId: number) => {
        const updatedDocuments = documents.filter(d => d.id !== documentId);
        onDocumentsUpdate(updatedDocuments);
    };

    const getDocumentByType = (type: string): Document | undefined => {
        return documents.find(d => d.type === type);
    };

    const getRequiredDocumentsStatus = () => {
        const requiredTypes = documentTypes.filter(dt => dt.required);
        const uploadedRequiredDocs = requiredTypes.filter(rt => {
            const doc = getDocumentByType(rt.type);
            return doc && doc.status !== 'REJECTED';
        });

        return {
            total: requiredTypes.length,
            uploaded: uploadedRequiredDocs.length,
            isComplete: uploadedRequiredDocs.length === requiredTypes.length
        };
    };

    const getOptionalDocumentsStatus = () => {
        // Считаем все дополнительные документы (POWER_OF_ATTORNEY и ADDITIONAL_DOCUMENT)
        const additionalDocs = documents.filter(doc =>
            (doc.type === 'POWER_OF_ATTORNEY' || doc.type === 'ADDITIONAL_DOCUMENT') &&
            doc.status !== 'REJECTED'
        );

        return {
            total: 10, // Максимум 10 дополнительных документов
            uploaded: additionalDocs.length
        };
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    const requiredStatus = getRequiredDocumentsStatus();
    const optionalStatus = getOptionalDocumentsStatus();

    // Отладочная информация
    console.log('DocumentsStep render:', {
        documents,
        documentTypes,
        requiredStatus,
        optionalStatus
    });

    return (
        <div className="space-y-6">
            {/* Статус загрузки */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={`p-4 rounded-lg border ${requiredStatus.isComplete
                    ? 'bg-green-50 border-green-200'
                    : 'bg-yellow-50 border-yellow-200'
                    }`}>
                    <div className="flex items-center">
                        {requiredStatus.isComplete ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                        ) : (
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mr-2" />
                        )}
                        <div>
                            <h3 className={`font-medium ${requiredStatus.isComplete ? 'text-green-800' : 'text-yellow-800'
                                }`}>
                                Обязательные документы
                            </h3>
                            <p className={`text-sm ${requiredStatus.isComplete ? 'text-green-700' : 'text-yellow-700'
                                }`}>
                                {requiredStatus.uploaded} из {requiredStatus.total} загружено
                            </p>
                        </div>
                    </div>
                </div>

                <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
                    <div className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-2" />
                        <div>
                            <h3 className="font-medium text-blue-800">
                                Дополнительные документы
                            </h3>
                            <p className="text-sm text-blue-700">
                                {optionalStatus.uploaded} из {optionalStatus.total} загружено
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Загруженные документы */}
            {documents.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                        Загруженные документы ({documents.length})
                    </h3>
                    <div className="space-y-3">
                        {documents.map((doc) => {
                            const docType = documentTypes.find(dt => dt.type === doc.type);
                            return (
                                <div key={doc.id} className="bg-white rounded-lg p-4 border border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <h4 className="text-sm font-medium text-gray-900">
                                                    {docType?.description || doc.type}
                                                </h4>
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${doc.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                                                    doc.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                                                        doc.status === 'NEEDS_REVISION' ? 'bg-orange-100 text-orange-800' :
                                                            doc.status === 'PENDING_MODERATION' ? 'bg-yellow-100 text-yellow-800' :
                                                                'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {doc.status === 'APPROVED' && 'Одобрен'}
                                                    {doc.status === 'REJECTED' && 'Отклонен'}
                                                    {doc.status === 'NEEDS_REVISION' && 'Требует доработки'}
                                                    {doc.status === 'PENDING_MODERATION' && 'На модерации'}
                                                    {doc.status === 'DRAFT' && 'Черновик'}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 truncate">
                                                {doc.fileName}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {formatFileSize(doc.fileSize)} • Загружен {new Date(doc.uploadedAt).toLocaleString('ru-RU')}
                                            </p>
                                            {doc.rejectionReason && (
                                                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                                                    <p className="text-sm text-red-700">
                                                        <strong>Причина отклонения:</strong> {doc.rejectionReason}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center space-x-2 ml-4">
                                            <button
                                                type="button"
                                                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                                onClick={() => {
                                                    if (doc.fileUrl) {
                                                        window.open(doc.fileUrl, '_blank');
                                                    }
                                                }}
                                                title="Просмотреть документ"
                                            >
                                                <EyeIcon className="h-5 w-5" />
                                            </button>
                                            {!isReadOnly && (doc.status === 'REJECTED' || doc.status === 'NEEDS_REVISION' || doc.status === 'PENDING_MODERATION') && (
                                                <button
                                                    type="button"
                                                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                                    onClick={() => handleDeleteSuccess(doc.id)}
                                                    title="Удалить документ"
                                                >
                                                    <TrashIcon className="h-5 w-5" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Обязательные документы */}
            <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    Обязательные документы
                    <span className="ml-2 text-red-500">*</span>
                </h3>
                <div className="space-y-4">
                    {documentTypes.filter(dt => dt.required).map((docType) => (
                        <DocumentUpload
                            key={docType.type}
                            documentType={docType.type}
                            typeDescription={docType.description}
                            isRequired={docType.required}
                            existingDocument={getDocumentByType(docType.type)}
                            onUploadSuccess={handleUploadSuccess}
                            onDeleteSuccess={handleDeleteSuccess}
                            disabled={isReadOnly}
                        />
                    ))}
                </div>
            </div>

            {/* Дополнительные документы */}
            <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Дополнительные документы
                </h3>
                <MultipleDocumentUpload
                    existingDocuments={documents.filter(doc =>
                        doc.type === 'POWER_OF_ATTORNEY' || doc.type === 'ADDITIONAL_DOCUMENT'
                    )}
                    onUploadSuccess={handleUploadSuccess}
                    onDeleteSuccess={handleDeleteSuccess}
                    disabled={isReadOnly}
                    maxFiles={10}
                />
            </div>

            {/* Инструкции */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Требования к документам:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Формат файлов: только PDF</li>
                    <li>• Максимальный размер файла: 50 МБ</li>
                    <li>• Документы должны быть четкими и читаемыми</li>
                    <li>• Все обязательные документы должны быть загружены для продолжения</li>
                    <li>• Можно загрузить до 10 дополнительных документов (доверенности, сертификаты и т.д.)</li>
                </ul>
            </div>

            {!requiredStatus.isComplete && !isReadOnly && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mr-2" />
                        <div>
                            <h4 className="font-medium text-yellow-800">
                                Не все обязательные документы загружены
                            </h4>
                            <p className="text-sm text-yellow-700 mt-1">
                                Загрузите все обязательные документы для перехода к следующему шагу.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {isReadOnly && (
                <div className="bg-gray-50 border border-gray-200 rounded p-4">
                    <p className="text-sm text-gray-600 text-center">
                        Документы (только для просмотра)
                    </p>
                </div>
            )}
        </div>
    );
};
